import React from 'react';
import { gsap } from 'gsap';
import cn from 'classnames';
import { useSwipeable, SwipeEventData, SwipeableHandlers } from 'react-swipeable';
import { BBCommonProps, BBEditableModeProps } from 'types/story';
import { sleep } from 'common/utils/helpers';
import { COMPONENT_STATES, UNIFORM_PROPS } from 'common/constants';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { EventProviderContextT, withEventProvider } from 'client/components/pages/Story/EventProvider/Context';
import { AUTO_HEIGHT_ATTR } from 'client/components/common/BuildingBlocks/Content/AutoHeight';
import css from './Swipe.scss';

type Props = BBCommonProps & EventProviderContextT;

const xDeltaMax = 50;
const rMax = 30;
const rSpeed = rMax / (window.innerWidth * 0.75);
const timeout = {
	out: 0.7,
	in: 0.3,
};

const Swipeable = props => {
	const handlers = useSwipeable({
		onSwiping: props.onSwiping,
		onSwiped: props.onSwiped,
		trackMouse: true,
	});

	return props.children(handlers);
};

class Swipe extends React.Component<Props> {
	swiped: boolean = false;

	componentDidMount() {
		// Register component events in provider, potentially Answer BB can use it.
		// "left" and "right" values is an ID of some component
		this.props.eventProvider.setComponentEvents(this.onSwipeProp.left, {
			onSwipe: () => this._onSwiped({ dir: 'Left' }),
		});
		this.props.eventProvider.setComponentEvents(this.onSwipeProp.right, {
			onSwipe: () => this._onSwiped({ dir: 'Right' }),
		});
	}

	get onSwipeProp() {
		return this.props.uiConfig.componentProps[UNIFORM_PROPS.onSwipe]!;
	}

	setAutoHeightByContentBB = (action: 'off' | 'on') => {
		const c: HTMLDivElement | undefined = this.props.containerRef?.current;
		if (!c) return;

		switch (action) {
			case 'off':
				c.setAttribute(AUTO_HEIGHT_ATTR.name, AUTO_HEIGHT_ATTR.value.preserve);
				break;
			case 'on':
				c.removeAttribute(AUTO_HEIGHT_ATTR.name);
				break;
			default:
				break;
		}
	};

	_animate = (vars: gsap.TweenVars) => {
		if (this.props.containerRef) {
			// disable css transition because of conflict with "card expose" inherited transition
			gsap.set(this.props.containerRef?.current, { css: { transition: 'none' } });
			gsap.to(this.props.containerRef.current, vars);
		}
	};

	_onSwiping = (eventData: SwipeEventData) => {
		if (this.swiped) return;

		if (this.props.containerRef) {
			const { deltaX } = eventData;
			const x = Math.abs(deltaX) > xDeltaMax ? xDeltaMax * (deltaX < 0 ? -1 : 1) : deltaX;
			const r = rSpeed * deltaX;

			// move to swipe delta
			this._animate({ rotation: r, x, onStart: () => this.setAutoHeightByContentBB('off') });
		}
	};

	_onSwiped = ({ dir, deltaX }: SwipeEventData | { dir: SwipeEventData['dir']; deltaX?: number }) => {
		const action = () => {
			this.swiped = true;

			switch (dir) {
				case 'Left':
					this.onSwipeLeft();
					break;
				case 'Right':
					this.onSwipeRight();
					break;
				default:
					break;
			}
		};

		// --> Discard
		if (deltaX !== undefined && Math.abs(deltaX) < xDeltaMax) {
			// animate to orin position
			this._animate({
				x: 0,
				rotation: 0,
				duration: 0.2,
				ease: 'power1.easeIn',
				overwrite: true,
				onComplete: () => this.setAutoHeightByContentBB('on'),
			});
			return;
		}

		// --> Apply
		const d = dir === 'Right' ? 1 : -1;
		this._animate({
			rotation: rMax * d,
			x: window.innerWidth * 0.5 * d,
			opacity: 0,
			duration: timeout.out,
			ease: 'expo.out',
			overwrite: true,
			onStart: () => {
				this.setAutoHeightByContentBB('off');
				action();
			},
			onComplete: () => {
				gsap.set(this.props.containerRef!.current, { x: 0, y: 0, rotate: 0, opacity: 0 });
				this.setAutoHeightByContentBB('on');
			},
		});
	};

	onSwipeLeft = () => {
		return this.onSwipe('Left');
	};

	onSwipeRight = () => {
		return this.onSwipe('Right');
	};

	onSwipe = async (side: 'Left' | 'Right') => {
		this.props.setStates({ [COMPONENT_STATES.SELECTED]: true });

		await sleep(timeout.out * 1000 * 0.5);

		// Call event handler of component that is bound to current Swipe action
		this.props.eventProvider.events.component[this.onSwipeProp[side.toLowerCase()]]?.onClick?.('swipe');
	};

	renderEditable() {
		const props = { ...this.props.editableModeProps?.nodeProps };
		return this.renderDefault(props);
	}

	renderDefault(props: Partial<BBEditableModeProps['nodeProps'] & SwipeableHandlers>) {
		const { nodeProps } = this.props.uiConfig;
		const className = cn(css.swipe, nodeProps.className, props.className);

		return (
			<div
				ref={this.props.containerRef}
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				{...props}
				className={className}
			>
				{this.props.children}
			</div>
		);
	}

	render() {
		return this.props.isEditableMode ? (
			this.renderEditable()
		) : (
			<Swipeable onSwiped={this._onSwiped} onSwiping={this._onSwiping}>
				{(handlers: SwipeableHandlers) =>
					this.renderDefault({
						...handlers,
						ref: el => {
							handlers.ref(el);
							if (this.props.containerRef) this.props.containerRef.current = el;
						},
					})
				}
			</Swipeable>
		);
	}
}

export default withEventProvider(withCardTransitionContext(Swipe));
